import { CommonModule } from '@angular/common';
import { ListService, LocalizationPipe } from '@abp/ng.core';
import {
  CardBodyComponent,
  CardComponent,
  ConfirmationService,
  ModalComponent,
} from '@abp/ng.theme.shared';
import { OnInit, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Column } from 'angular-slickgrid';
import {
  BaseCrudComponent,
  CrudPage,
  DEFAULT_MAX_RESULT_COUNT,
  FieldMapperHelper,
  ICrudService,
  localizeWithFallback,
  ZnButton,
  ZnSingleSelect,
} from '@zendra/shared';
import {
  ForestryBasePlanningOrchestratorService,
  type ForestryBaseVariationDto,
  type ForestryBaseVariationsExcelDownloadDto,
} from 'orchestrator';
import { finalize, firstValueFrom, map, Observable, of, throwError } from 'rxjs';
import { AvailableReportDateDto } from '../proxy/geographic/dtos/forestry-base';
import { ForestryBaseService } from '../proxy/geographic/controllers';
import {
  FORESTRYBASE_VARIATIONS_COLUMN_GROUPS,
  FORESTRYBASE_VARIATIONS_CONFIG,
  FORESTRYBASE_VARIATIONS_FIELDS,
  FORESTRYBASE_VARIATIONS_TABLE_OPTIONS,
} from './forestry-base-variations.config';

interface ReportDateOption {
  key: string;
  value: string;
}

interface ExecutedParameters {
  initialReportDate: string;
  finalReportDate: string;
  includeNonPlantableAreas: boolean;
}

type UnsupportedCrudDto = Record<string, never>;
type ForestryBaseVariationRow = ForestryBaseVariationDto & { id: number };

const VARIATION_TYPE_LOCALIZATION_PREFIX =
  'Geographic::Entity.ForestryBase.Variations.Type.';

@Component({
  selector: 'app-forestry-base-variations',
  imports: [
    CommonModule,
    CardComponent,
    CardBodyComponent,
    CrudPage,
    LocalizationPipe,
    ModalComponent,
    ZnButton,
    ZnSingleSelect,
    FormsModule,
  ],
  providers: [ListService, ForestryBaseService, ConfirmationService],
  templateUrl: './forestry-base-variations.html',
  styleUrl: './forestry-base-variations.scss',
})
export class ForestryBaseVariations extends BaseCrudComponent<
  ForestryBaseVariationRow,
  UnsupportedCrudDto,
  UnsupportedCrudDto,
  ForestryBaseVariationsExcelDownloadDto
> implements OnInit {
  private readonly forestryBaseService = inject(ForestryBaseService);
  private readonly orchestratorService = inject(ForestryBasePlanningOrchestratorService);

  readonly config = FORESTRYBASE_VARIATIONS_CONFIG;
  readonly fields = FORESTRYBASE_VARIATIONS_FIELDS;
  readonly tableOptions = FORESTRYBASE_VARIATIONS_TABLE_OPTIONS;
  readonly columns = this.buildColumns();
  readonly modalOptions = {
    centered: true,
    size: 'md' as const,
  };

  readonly availableReportDates = signal<ReportDateOption[]>([]);
  readonly loadingReportDates = signal(false);
  readonly showParametersModal = signal(true);
  readonly queryExecuted = signal(false);
  readonly totalCount = signal(0);
  readonly executedParameters = signal<ExecutedParameters | null>(null);
  readonly initialReportDate = signal<string | null>(null);
  readonly finalReportDate = signal<string | null>(null);
  readonly includeNonPlantableAreas = signal(false);
  readonly validationMessage = signal('');

  readonly hasInvalidDateRange = computed(() =>
    this.isDateRangeInvalid(this.initialReportDate(), this.finalReportDate()),
  );
  readonly canExecuteQuery = computed(
    () =>
      !!this.initialReportDate() &&
      !!this.finalReportDate() &&
      !this.hasInvalidDateRange(),
  );
  readonly comparisonPeriodText = computed(() => {
    const parameters = this.executedParameters();
    if (!parameters) {
      return '';
    }

    return `${this.formatDisplayDate(parameters.initialReportDate)} - ${this.formatDisplayDate(parameters.finalReportDate)}`;
  });
  readonly executedIncludeNonPlantableAreas = computed(
    () => this.executedParameters()?.includeNonPlantableAreas ?? false,
  );

  service: ICrudService<
    ForestryBaseVariationRow,
    UnsupportedCrudDto,
    UnsupportedCrudDto,
    ForestryBaseVariationsExcelDownloadDto
  > = {
    create: (_input: UnsupportedCrudDto) => this.unsupportedCrudOperation('create'),
    update: (_id: number, _input: UnsupportedCrudDto) => this.unsupportedCrudOperation('update'),
    deleteByIds: (_ids: number[]) => this.unsupportedCrudOperation('delete'),
    getList: query => {
      const parameters = this.executedParameters();

      if (!parameters) {
        return of({
          items: [],
          totalCount: 0,
        });
      }

      return this.orchestratorService.getVariations({
        ...query,
        ...parameters,
      }).pipe(
        map(response => {
          const skipCount = query?.skipCount ?? 0;
          const items = (response.items ?? []).map((item, index) => ({
            ...item,
            id: skipCount + index + 1,
          }));

          return {
            items,
            totalCount: response.totalCount ?? 0,
          };
        }),
      );
    },
    getDownloadToken: () => of({ token: '' }),
    getListAsExcelFile: input => this.orchestratorService.getVariationsAsExcelFile(input),
  };

  override loadData = (
    queryString: string,
  ): Observable<{ data: ForestryBaseVariationRow[]; totalCount: number }> => {
    this.loading.set(true);

    const params = this.queryBuilder.buildQueryParams(queryString);
    return this.service.getList(params).pipe(
      map(response => {
        const data = response.items ?? [];
        const totalCount = response.totalCount ?? 0;

        this.dataset.set(data);
        this.totalCount.set(totalCount);

        return {
          data,
          totalCount,
        };
      }),
      finalize(() => {
        this.loading.set(false);
      }),
    );
  };

  ngOnInit(): void {
    void this.loadAvailableReportDates();
  }

  async loadAvailableReportDates(): Promise<void> {
    this.loadingReportDates.set(true);

    try {
      const response = await firstValueFrom(
        this.forestryBaseService.getAvailableReportDates({
          skipCount: 0,
          maxResultCount: DEFAULT_MAX_RESULT_COUNT,
        }),
      );

      this.availableReportDates.set(
        this.mapReportDateOptions(response.items ?? []),
      );
    } finally {
      this.loadingReportDates.set(false);
    }
  }

  openParametersModal(): void {
    this.showParametersModal.set(true);
  }

  onParametersModalVisibleChange(visible: boolean): void {
    this.showParametersModal.set(visible);
  }

  onInitialReportDateChange(value: string | null): void {
    this.initialReportDate.set(this.normalizeDateKey(value));
    this.updateValidationMessage();
  }

  onFinalReportDateChange(value: string | null): void {
    this.finalReportDate.set(this.normalizeDateKey(value));
    this.updateValidationMessage();
  }

  onIncludeNonPlantableAreasChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.includeNonPlantableAreas.set(!!target.checked);
  }

  runQuery(): void {
    this.updateValidationMessage();

    if (!this.canExecuteQuery()) {
      return;
    }

    this.executedParameters.set({
      initialReportDate: this.initialReportDate()!,
      finalReportDate: this.finalReportDate()!,
      includeNonPlantableAreas: this.includeNonPlantableAreas(),
    });
    this.queryExecuted.set(true);
    this.totalCount.set(0);
    this.showParametersModal.set(false);
    this.refreshQueryResults();
  }

  async exportToExcel(): Promise<void> {
    const parameters = this.executedParameters();
    if (!parameters) {
      return;
    }

    this.loading.set(true);

    try {
      const queryParams = this.queryBuilder.buildQueryParams(this.getCurrentQueryString());
      delete queryParams.skipCount;
      delete queryParams.maxResultCount;

      const file = await firstValueFrom(
        this.orchestratorService.getVariationsAsExcelFile({
          ...parameters,
          ...queryParams,
          maxResultCount: Math.max(this.totalCount(), 1),
          fields: this.getVisibleFields(),
        }),
      );

      this.downloadFile(file, this.getEntityName());
    } finally {
      this.loading.set(false);
    }
  }

  formatDisplayDate(value?: string | null): string {
    const normalized = this.normalizeDateKey(value);
    if (!normalized) {
      return '';
    }

    const parts = normalized.split('-');
    if (parts.length !== 3) {
      return normalized;
    }

    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  getIncludeNonPlantableAreasText(includeNonPlantableAreas: boolean): string {
    return localizeWithFallback(
      this.localizationService,
      includeNonPlantableAreas
        ? 'Geographic::Entity.ForestryBase.Variations.Included'
        : 'Geographic::Entity.ForestryBase.Variations.Excluded',
    );
  }

  protected prepareUpdateDto(
    _data: any,
    _selected: ForestryBaseVariationRow,
  ): UnsupportedCrudDto {
    return {};
  }

  protected getEntityName(): string {
    return 'ForestryBaseVariations';
  }

  private buildColumns(): Column[] {
    const columnGroupMap = new Map<
      string,
      { label: string; labelKey: string }
    >();

    FORESTRYBASE_VARIATIONS_COLUMN_GROUPS.forEach(group => {
      const label = localizeWithFallback(this.localizationService, group.labelKey);

      group.fieldKeys.forEach(fieldKey => {
        columnGroupMap.set(fieldKey, {
          label,
          labelKey: group.labelKey,
        });
      });
    });

    return FieldMapperHelper.buildColumnsFromFields(
      this.fields,
      this.localizationService,
      this.injector,
    ).map(column => {
      const group =
        typeof column.field === 'string' ? columnGroupMap.get(column.field) : undefined;

      return {
        ...column,
        excludeFromGridMenu: true,
        excludeFromColumnPicker: true,
        ...(group
          ? {
              columnGroup: group.label,
              columnGroupKey: group.labelKey,
            }
          : {}),
        ...(column.field === 'variationType'
          ? {
              formatter: (_row, _cell, value) => this.localizeVariationType(value),
            }
          : {}),
      };
    });
  }

  private refreshQueryResults(): void {
    const backendService = this.crudPage?.gridTable?.gridOptions?.backendServiceApi?.service as
      | { resetPaginationOptions?: () => void }
      | undefined;

    backendService?.resetPaginationOptions?.();
    this.refreshGrid();
  }

  private getCurrentQueryString(): string {
    const backendService = this.crudPage?.gridTable?.gridOptions?.backendServiceApi?.service as
      | { buildQuery?: () => string }
      | undefined;

    return backendService?.buildQuery?.() ?? '{}';
  }

  private getVisibleFields(): string[] {
    const visibleColumns = this.crudPage?.gridTable?.grid?.slickGrid?.getVisibleColumns?.() ?? [];

    return visibleColumns
      .filter(
        column =>
          typeof column.field === 'string' &&
          column.field.trim() !== '' &&
          column.field !== 'id' &&
          column.params?.excludeFromExport !== true,
      )
      .map(column => {
        const displayNestedPath = column.params?.displayNestedPath as
          | string
          | undefined;

        return displayNestedPath && displayNestedPath.trim() !== ''
          ? displayNestedPath
          : column.field;
      });
  }

  private unsupportedCrudOperation<T>(operation: string): Observable<T> {
    return throwError(
      () => new Error(`Forestry base variations does not support ${operation}.`),
    );
  }

  private localizeVariationType(value: unknown): string {
    if (typeof value !== 'string' || value.trim() === '') {
      return '';
    }

    const localizationKey = `${VARIATION_TYPE_LOCALIZATION_PREFIX}${value}`;
    const localizedValue = localizeWithFallback(
      this.localizationService,
      localizationKey,
    );

    return localizedValue === localizationKey ? value : localizedValue;
  }

  private mapReportDateOptions(items: AvailableReportDateDto[]): ReportDateOption[] {
    return items.flatMap(item => {
      const key = this.normalizeDateKey(item.date);
      if (!key) {
        return [];
      }

      return [{ key, value: this.formatDisplayDate(key) }];
    });
  }

  private normalizeDateKey(value?: string | null): string | null {
    if (!value) {
      return null;
    }

    return value.substring(0, 10);
  }

  private updateValidationMessage(): void {
    this.validationMessage.set(
      this.hasInvalidDateRange() ? this.getInvalidDateRangeMessage() : '',
    );
  }

  private isDateRangeInvalid(
    initialReportDate: string | null,
    finalReportDate: string | null,
  ): boolean {
    return !!initialReportDate && !!finalReportDate && finalReportDate <= initialReportDate;
  }

  private getInvalidDateRangeMessage(): string {
    const key = 'Geographic::Entity.ForestryBase.Variations.InvalidDateRange';
    const localizedMessage = localizeWithFallback(this.localizationService, key);

    return localizedMessage === key
      ? 'The final date must be greater than the initial date'
      : localizedMessage;
  }
}
